import { v7 as uuidv7 } from 'uuid';
import { looksLikeDate } from './DateTools';
import { BasePageModel, getPageData } from '#utilities/GetPageData';

export type WithStatus<T> = T & {
  httpStatusCode?: number;
  httpStatusText?: string;
};

export const ApiClient = {
  get,
  post,
  postFormData,
};

globalThis.interactionId = globalThis.interactionId || uuidv7();

function getUserAgent(): string {
  return String(
    globalThis.userAgent ??
      globalThis.navigator?.userAgent ??
      (typeof navigator === 'object' ? navigator.userAgent : 'unknown')
  );
}

/**
 * Makes a GET request to the specified URL.
 * @param {string} url - The URL to request.
 * @param {Record<string, string>} params - The parameters of the request.
 * @returns {Promise<T>}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function get<T = any>(url: string, params?: Record<string, string>): Promise<T> {
  const requestOptions: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-Interaction-Id': globalThis.interactionId,
    },
  };
  return handleResponse(params ? `${url}?${new URLSearchParams(params)}` : url, requestOptions);
}

/**
 * Makes a POST request to the specified URL with the provided body.
 * @param {string} url - The URL to request.
 * @param {T} body - The body of the request.
 * @param {Record<string, string>} headers - Extra headers to send the with request.
 * @param {AbortSignal} signal - optional - An abort signal.
 * @param {boolean} disableReviver - optional - Disables the automatic conversation of string -> dates.
 * @returns {Promise<R>}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function post<R = any, T = unknown>(
  url: string,
  body: T,
  headers?: Record<string, string>,
  signal?: AbortSignal,
  disableReviver?: boolean
): Promise<R> {
  const requestOptions: RequestInit = {
    method: 'POST',
    redirect: 'error',
    headers: {
      'Content-Type': 'application/json',
      'X-Interaction-Id': globalThis.interactionId,
      'X-User-Agent': getUserAgent(),
      ...(headers ?? {}),
    },
    body: JSON.stringify(body),
    signal,
  };
  return handleResponse(url, requestOptions, disableReviver);
}

/**
 * Makes a multipart/form-data POST request to the specified URL.
 * Do not manually set Content-Type here; the browser will set the boundary.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function postFormData<R = any>(
  url: string,
  body: FormData,
  headers?: Record<string, string>,
  signal?: AbortSignal,
  disableReviver?: boolean
): Promise<R> {
  const requestOptions: RequestInit = {
    method: 'POST',
    redirect: 'error',
    headers: {
      'X-Interaction-Id': globalThis.interactionId,
      ...(headers ?? {}),
    },
    body,
    signal,
  };

  return handleResponse(url, requestOptions, disableReviver);
}

/**
 * @param {string} url
 * @returns {string}
 */
export function buildUrl(url: string): string {
  const baseUrl = getPageData<BasePageModel>().ApiBaseUrl;
  if (url.startsWith('.') || url.startsWith('http')) {
    return url;
  }

  // NOTE: this assumes that baseUrl ends with a /
  if (url.startsWith('/')) {
    return `${baseUrl}${url.substring(1)}`;
  }

  return `${baseUrl}${url}`;
}

/**
 * Handles the response from a fetch request.
 * @param {string} url
 * @param {RequestInit | undefined} requestOptions
 * @param {boolean} disableReviver - optional - Disables the automatic conversation of string -> dates.
 * @returns {Promise<any>}
 */
async function handleResponse(
  url: string,
  requestOptions: RequestInit | undefined,
  disableReviver?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  const fullUrl = buildUrl(url);

  const response = await fetch(fullUrl, requestOptions);

  let data: object = undefined;
  try {
    const text = await response.text();
    data = text && JSON.parse(text, disableReviver ? undefined : reviver);
  } catch (e: unknown) {
    console.log('Error parsing server response', e);
  }

  if (!response.ok) {
    const error = { data };
    return Promise.reject(augmentReturnData(response, error));
  }

  return augmentReturnData(response, data);
}

/**
 * Put a bare minimum of response data on the return type that we can handle status codes on the recieving end
 *
 * @param response The response object
 * @param returnData The object to be wrapped in response data goodness
 * @returns WithStatus<unknown>
 */
function augmentReturnData(response: Response, returnData: object) {
  if (typeof returnData == 'boolean') {
    returnData = {
      Success: returnData,
    };
  }

  return {
    ...(returnData ?? {}),
    httpStatusCode: response.status,
    httpStatusText: response.statusText,
  } as WithStatus<unknown>;
}

function reviver(this: unknown, key: string, value: unknown | string) {
  if (typeof value === 'string' && key.toLowerCase().endsWith('date') && (value ?? false) && looksLikeDate(value)) {
    const valueAsDate = new Date(value);
    if (!isNaN(valueAsDate.getTime())) {
      return valueAsDate;
    }
  }

  return value;
}